/**
 * How risky an approval looks, in plain words. A fixed table of patterns, never a guess by a model: the same request always gets
 * the same answer, on the phone and on the computer. When it is unsure it says "Check this", it never says "Low risk".
 *
 *  low      only looks, or runs the project's own tests, linters and builds
 *  check    changes something, but in the usual way a coding session does (edits, installs, commits, pushes)
 *  careful  can destroy work, reach secrets, run with admin rights or run code it downloads
 */
export type RiskLevel = "low" | "check" | "careful";

export interface Risk {
  level: RiskLevel;
  /** A few words on what it will do: "Runs your tests", "Deletes files". */
  headline: string;
}

export const RISK_LABEL: Record<RiskLevel, string> = { low: "Low risk", check: "Check this", careful: "Careful" };
const ORDER: Record<RiskLevel, number> = { low: 0, check: 1, careful: 2 };

const mk = (level: RiskLevel, headline: string): Risk => ({ level, headline });

/** Tools that run a shell command. */
export const SHELL_TOOLS: ReadonlySet<string> = new Set(["Bash", "shell", "local_shell", "exec_command", "container.exec"]);
/** Tools that write a file at a path. */
export const FILE_EDIT_TOOLS: ReadonlySet<string> = new Set(["Edit", "MultiEdit", "Write", "NotebookEdit"]);
const FILE_READ_TOOLS: ReadonlySet<string> = new Set(["Read", "Glob", "Grep", "LS", "NotebookRead"]);

/** Paths that hold secrets or control the machine. */
const SENSITIVE_PATH =
  /(^|[\\/\s"'=])\.env(\.(?!example\b|sample\b|template\b|dist\b)[\w.-]+)?($|[\\/\s"'])|[\\/]\.ssh[\\/]|id_(rsa|ed25519|ecdsa|dsa)\b|[\\/]\.aws[\\/]|[\\/]\.gnupg[\\/]|(^|[\\/\s"'])\.(npmrc|netrc|pgpass|git-credentials)\b|\.(pem|p12|pfx|key)($|[\s"'])|^\/etc\/|[\\/]\.git[\\/](config|hooks)\b|(^|[\\/\s"'])(credentials|secrets?)(\.(json|ya?ml|toml|txt))?($|[\s"'])|keychain/i;

export const touchesSecrets = (s: string): boolean => SENSITIVE_PATH.test(s);

interface Hit {
  re: RegExp;
  headline: string;
}

// Checked against the whole command (so a dangerous part can't hide behind a harmless one).
const CAREFUL: Hit[] = [
  { re: /(^|[\s;&|(])(sudo|doas)\b|(^|[\s;&|(])su\s+(-\w*\s+)?\w*(\s|$)/, headline: "Runs with admin rights" },
  { re: /\brm\b[^;&|\n]*(\s-[a-z]*[rf][a-z]*\b|\s--(recursive|force)\b)/, headline: "Deletes files" },
  { re: /\brm\b[^;&|\n]*\s(\/|~|\$HOME|\*)(\s|$)/, headline: "Deletes files" },
  { re: /\bfind\b[^;&|\n]*(\s-delete\b|\s-exec\s+rm\b)/, headline: "Deletes files" },
  { re: /\b(rmdir|shred|unlink)\b/, headline: "Deletes files" },
  { re: /\bgit\s+push\b[^;&|\n]*(\s--force(-with-lease)?\b|\s-f\b|\s\+\S)/, headline: "Force-pushes, which can overwrite others' work" },
  { re: /\bgit\s+reset\b[^;&|\n]*\s--hard\b/, headline: "Throws away your changes" },
  { re: /\bgit\s+clean\b[^;&|\n]*\s-\w*[fdx]/, headline: "Throws away your changes" },
  { re: /\bgit\s+(checkout|restore)\b[^;&|\n]*\s(--\s+)?\.(\s|$)/, headline: "Throws away your changes" },
  { re: /\bgit\s+branch\b[^;&|\n]*\s-D\b/, headline: "Deletes a branch" },
  { re: /\|\s*(sudo\s+)?(ba|z|da|k|c|fi)?sh\b|\|\s*(sudo\s+)?(python3?|node|perl|ruby)\b/, headline: "Runs a script it downloads" },
  { re: /\b(sh|bash|zsh)\s+<\(\s*(curl|wget)\b|\beval\b.*\b(curl|wget)\b/, headline: "Runs a script it downloads" },
  { re: /\bchmod\b[^;&|\n]*\s-\w*R|\bchown\b[^;&|\n]*\s-\w*R|\bchmod\s+(-\w+\s+)?0?777\b/, headline: "Changes file permissions widely" },
  { re: /\b(dd|mkfs(\.\w+)?|fdisk|parted|wipefs)\b|\bdiskutil\s+(erase|partition|zero)\w*|>\s*\/dev\/(sd|nvme|disk|hd)/, headline: "Writes straight to a disk" },
  { re: /\b(drop\s+(table|database|schema)|truncate\s+table|delete\s+from)\b/i, headline: "Changes a database" },
  { re: /\b(kubectl\s+delete|terraform\s+(destroy|apply\b[^;&|\n]*-auto-approve)|docker\s+system\s+prune|docker\s+volume\s+(rm|prune)|helm\s+(uninstall|delete))\b/, headline: "Deletes infrastructure or data" },
  { re: /\b(aws|gcloud|az|doctl|heroku|fly|flyctl|wrangler)\b[^;&|\n]*\b(delete|destroy|terminate|remove|rm)\b/, headline: "Deletes something in the cloud" },
  { re: /\b(npm|pnpm|yarn)\s+(publish|unpublish|deprecate)\b|\bcargo\s+publish\b|\btwine\s+upload\b|\bgem\s+push\b/, headline: "Publishes a package" },
  { re: /\b(shutdown|reboot|halt|poweroff)\b|:\(\)\s*\{|\bkill\s+(-9\s+)?-1\b|\bkillall\s+-9\b/, headline: "Can stop or break your computer" },
  { re: /\b(printenv|env|set)\b\s*(\||>|$)|\bsecurity\s+(find|dump|export)-|\bcat\s+[^;&|\n]*\/etc\/(passwd|shadow)/, headline: "Looks at secrets" },
];

const CHECK: Hit[] = [
  { re: /\bgit\s+push\b/, headline: "Pushes your commits" },
  { re: /\bgit\s+commit\b/, headline: "Saves a commit" },
  { re: /\bgit\s+(checkout|switch|merge|rebase|pull|fetch|stash|cherry-pick|revert|reset|tag|restore|worktree|submodule|apply|am|mv|rm|branch)\b/, headline: "Changes your branch or files" },
  { re: /\b(npm|pnpm|yarn|bun)\s+(install|i|add|remove|rm|uninstall|update|upgrade|link|ci)\b|\b(pip3?|pipx|uv|poetry|conda)\s+(install|uninstall|add|remove)\b|\b(brew|apt(-get)?|dnf|yum|pacman|choco|winget|scoop)\s+(install|uninstall|remove|upgrade)\b|\bcargo\s+(install|add|remove|update)\b|\bgo\s+(get|install)\b|\bgem\s+install\b/, headline: "Installs or removes packages" },
  { re: /\b(curl|wget|http|httpie)\b/, headline: "Talks to the internet" },
  { re: /\b(ssh|scp|sftp|rsync|nc|ncat|telnet)\b/, headline: "Connects to another machine" },
  { re: /\b(docker|podman|kubectl|helm|terraform)\b/, headline: "Works with containers or infrastructure" },
  { re: /\b(kill|pkill|killall|taskkill)\b/, headline: "Stops a process" },
  { re: /\b(chmod|chown|chgrp)\b/, headline: "Changes file permissions" },
  { re: /\b(mv|cp|ln|rename)\b/, headline: "Moves or copies files" },
  { re: /\b(mkdir|touch|mktemp)\b/, headline: "Creates files or folders" },
  { re: /\b(sed|perl)\s+(-\w*i\w*|-pi)\b|\btee\b/, headline: "Edits files in place" },
  { re: /\brm\b/, headline: "Deletes a file" },
  { re: /\b(npm|pnpm|yarn|bun)\s+(run\s+)?(dev|start|serve|deploy|release|migrate|seed|clean|reset)\b|\bmake\b|\b(node|python3?|ruby|perl|deno|bun|tsx|ts-node)\s+\S/, headline: "Runs a program or script" },
  { re: /\b(npx|pnpx|bunx|uvx|pipx\s+run)\b/, headline: "Runs a downloaded tool" },
];

// A segment made only of these is just looking around or running the project's own checks.
const LOW_SEGMENT: { re: RegExp; headline: string }[] = [
  { re: /^(npm|pnpm|yarn|bun)\s+(run\s+)?(test|tests|lint|typecheck|type-check|check|build|format:check|fmt:check|ci:quick|ci:local)(:\w+)?(\s|$)/, headline: "Runs your tests or build" },
  { re: /^(npm|pnpm|yarn|bun)\s+(ls|list|outdated|why|view|info|--version|-v|audit)(\s|$)/, headline: "Looks at your packages" },
  { re: /^(pytest|py\.test|vitest|jest|mocha|ava|tap|playwright\s+test|cypress\s+run)(\s|$)|^python3?\s+-m\s+(pytest|unittest)(\s|$)/, headline: "Runs your tests" },
  { re: /^cargo\s+(test|check|build|clippy|fmt|doc|tree|bench)(\s|$)|^go\s+(test|build|vet|fmt|list|mod\s+(tidy|verify))(\s|$)|^(tsc|eslint|prettier|biome|ruff|mypy|black|flake8|swiftlint)(\s|$)|^(gradlew?|\.\/gradlew)\s+(test|build|check)(\s|$)|^mvn\s+(test|verify|compile)(\s|$)/, headline: "Checks or builds your code" },
  { re: /^git\s+(status|diff|log|show|blame|ls-files|ls-remote|rev-parse|rev-list|describe|remote(\s+-v)?|config\s+--get|shortlog|reflog|add|stash\s+list|branch(\s+(-a|-r|-v|-vv|--list|--show-current))?|tag(\s+(-l|--list))?)(\s|$)/, headline: "Looks at your git history" },
  { re: /^(ls|ll|la|cat|head|tail|wc|pwd|tree|stat|file|du|df|which|where|type|whoami|date|uname|hostname|basename|dirname|realpath|readlink|sort|uniq|cut|tr|diff|cmp|jq|yq|grep|egrep|fgrep|rg|ag|ack|fd|nl|column|less|more|echo|printf|true|false|test|cd|pushd|popd|sleep|id|nproc|arch|sw_vers|lsof|ps|top|free|uptime)(\s|$)/, headline: "Looks around, changes nothing" },
  { re: /^find\s/, headline: "Looks around, changes nothing" },
  { re: /^sed\s+(-n|--quiet)\b/, headline: "Looks around, changes nothing" },
  { re: /^(node|python3?|ruby|go|rustc|cargo|java|npm|pnpm|yarn|bun|git|tsc|deno)\s+(--version|-v|-V|version)(\s|$)/, headline: "Checks a version" },
];

const splitCommand = (c: string): string[] =>
  c
    .split(/&&|\|\||;|\||\n|(?<![>&])&(?!>)/)
    .map((s) => s.trim())
    .filter(Boolean);

/** `CI=1 npm test` reads as `npm test`. */
const bare = (seg: string): string => {
  let s = seg.replace(/^(\s*\w+=(\S+|"[^"]*"|'[^']*')\s+)+/, "");
  s = s.replace(/^(time|nice|nohup|command)\s+/, "");
  return s.trim();
};

const worst = (a: Risk | null, b: Risk): Risk => (!a || ORDER[b.level] > ORDER[a.level] ? b : a);

/** A `>` that writes a file (not `2>&1`, `&>`/dev/null or `> /dev/null`). */
const writesFile = (c: string): boolean => /(^|[^>&0-9-])>{1,2}\s*[^&\s]/.test(c.replace(/\d?>\s*&\d|&>\s*\S+|\d?>{1,2}\s*\/dev\/null/g, ""));

function assessCommand(command: string): Risk {
  const c = command.trim();
  if (!c) return mk("check", "Runs a command");
  let top: Risk | null = null;
  for (const h of CAREFUL) if (h.re.test(c)) top = worst(top, mk("careful", h.headline));
  if (touchesSecrets(c)) top = worst(top, mk("careful", "Touches a secrets file"));
  if (top?.level === "careful") return top;

  const segments = splitCommand(c);
  let allLow = segments.length > 0;
  let lowHeadline: string | null = null;
  for (const raw of segments) {
    const seg = bare(raw);
    const low = LOW_SEGMENT.find((l) => l.re.test(seg));
    const checkHit = CHECK.find((h) => h.re.test(seg));
    if (low && (!checkHit || low.headline === "Checks a version")) {
      lowHeadline ??= low.headline;
      continue;
    }
    allLow = false;
    top = worst(top, mk("check", checkHit?.headline ?? "Runs a command"));
  }
  if (/\$\(|`/.test(c)) {
    allLow = false;
    top = worst(top, mk("check", "Runs a command inside a command"));
  }
  if (writesFile(c)) {
    allLow = false;
    top = worst(top, mk("check", "Writes to a file"));
  }
  if (/\bfind\b[^;&|\n]*\s-(exec|ok|execdir)\b/.test(c)) allLow = false;
  if (allLow) return mk("low", lowHeadline ?? "Looks around, changes nothing");
  return top ?? mk("check", "Runs a command");
}

/**
 * `tool` is what the agent asked to use (Bash, Edit, ...); `text` is what it will do: the whole command, the file path, the URL or the patch.
 * Give a shell tool the whole command, not the agent's note about it.
 */
export function assessRisk(tool: string, text: string | undefined): Risk {
  const t = (text ?? "").trim();
  if (SHELL_TOOLS.has(tool)) return assessCommand(t);
  if (FILE_READ_TOOLS.has(tool)) return touchesSecrets(t) ? mk("careful", "Reads a secrets file") : mk("low", "Reads files");
  if (FILE_EDIT_TOOLS.has(tool)) {
    if (touchesSecrets(t)) return mk("careful", "Changes a secrets or system file");
    return mk("check", tool === "Write" ? "Creates or overwrites a file" : "Edits a file");
  }
  switch (tool) {
    case "apply_patch":
      if (/\*\*\* Delete File:/i.test(t)) return mk("careful", "Deletes files");
      if (touchesSecrets(t)) return mk("careful", "Changes a secrets or system file");
      return mk("check", "Changes files");
    case "WebSearch":
      return mk("low", "Searches the web");
    case "WebFetch":
      return mk("check", "Fetches a web page");
    case "Task":
    case "Agent":
      return mk("check", "Starts a sub-agent");
    default:
      return /^mcp__/.test(tool) ? mk("check", "Uses a connected tool") : mk("check", "Uses a tool");
  }
}
